import { Alert, Button, Card, FormField, Input, Textarea } from '@ashniva/ui';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';

import { useAskQuestion } from '../api-questions';
import { classifyProblem } from '../ri-problems';
import { useSubmissionKey } from '../use-submission-key';
import { useWorkspace } from '../workspace-context';
import { ProblemNotice } from './ProblemNotice';
import { QuestionView } from './QuestionView';
import { RecentQuestions } from './RecentQuestions';

const MAX_QUESTION = 2_000;

/**
 * Ask about the code (P14-06, evidence mode): the question box, the mode it is answered in, then the answer as it
 * arrives. The open question's id is in the URL (`question`), so reloading or sharing the page reopens it for its
 * asker; the question itself never is.
 */
export function AskPanel() {
  const { linkId } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const questionId = params.get('question');
  const [question, setQuestion] = useState('');
  const [scope, setScope] = useState('');
  const ask = useAskQuestion(linkId);
  const keys = useSubmissionKey();

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const text = question.trim();
    // One submission at a time: a second click while the first is on its way sends nothing.
    if (!text || ask.isPending) {
      return;
    }
    const payload = {
      question: text,
      ...(scope.trim() ? { scope: { pathPrefix: scope.trim() } } : {}),
    };
    const clientRequestId = keys.start(payload);
    if (!clientRequestId) {
      return;
    }
    ask.mutate(
      { ...payload, clientRequestId },
      keys.callbacks((receipt: { question: { id: string } }) =>
        setParams((current) => {
          const next = new URLSearchParams(current);
          next.set('question', receipt.question.id);
          return next;
        }),
      ),
    );
  };

  return (
    <div className="ri-stack">
      <Card title="Ask about the code">
        <form className="ri-form" onSubmit={onSubmit} aria-label="Ask about the code">
          <p className="ri-muted">
            Evidence mode: the answer is put together from the indexed code itself, with a citation
            for every statement. No AI model writes it.
          </p>
          <FormField
            label="Your question"
            hint={`${question.length} of ${MAX_QUESTION} characters`}
          >
            <Textarea
              value={question}
              rows={4}
              maxLength={MAX_QUESTION}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </FormField>
          <FormField
            label="Only look in"
            optional
            hint="A directory or file, such as src/payroll. Narrowing helps when the answer is not found."
          >
            <Input
              value={scope}
              maxLength={400}
              onChange={(event) => setScope(event.target.value)}
            />
          </FormField>
          <div>
            <Button
              type="submit"
              variant="primary"
              loading={ask.isPending}
              disabled={!question.trim() || ask.isPending}
              disabledReason={
                ask.isPending ? 'Your question is being sent' : 'Write a question first'
              }
            >
              Ask
            </Button>
          </div>
          {ask.isError ? <AskError error={ask.error} /> : null}
        </form>
      </Card>
      {questionId ? <QuestionView questionId={questionId} /> : null}
      <RecentQuestions current={questionId} />
    </div>
  );
}

function AskError({ error }: { error: unknown }) {
  // A refused question is not a fault of the service: say what to change.
  if (classifyProblem(error) === 'invalid') {
    return (
      <Alert tone="danger" title="The question was not accepted">
        Check that it is under {MAX_QUESTION} characters and that the directory is a path inside the
        repository.
      </Alert>
    );
  }
  return <ProblemNotice error={error} size="sm" />;
}
